import React, { useEffect, useState } from "react";
import { UserCircle, Plus, Pencil, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, PageHeader, Button, Modal, Badge } from "@/components/common";

export default function Users() {
  const { branches, can, isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const canCreate = can("employees", "create") || isOwner;
  const canEdit = can("employees", "edit") || isOwner;

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/auth/users"), api.get("/roles").catch(() => [])]);
      setUsers(u); setRoles(r);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password ${u.name}?`)) return;
    try {
      const res = await api.post(`/auth/users/${u.id}/reset-password`);
      window.prompt("Password sementara (salin & berikan ke karyawan):", res.temp_password);
    } catch (e) { toast.error(e.message); }
  };

  const branchNames = (ids = []) => ids.map((id) => branches.find((b) => b.id === id)?.name || id).join(", ") || "Semua";

  return (
    <>
      <PageHeader title="Karyawan" subtitle={`${users.length} akun`} icon={UserCircle}
        actions={canCreate && <Button onClick={() => setEditing({})} data-testid="add-user-btn"><Plus size={16} /> Karyawan Baru</Button>} />

      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium">Nama</th>
                  <th className="py-3 px-4 font-medium">Peran</th>
                  <th className="py-3 px-4 font-medium">Cabang</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  {canEdit && <th className="py-3 px-4 font-medium text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">{u.name}</div>
                      <div className="text-xs text-slate-400">{u.email}{u.username ? ` · ${u.username}` : ""}</div>
                    </td>
                    <td className="py-3 px-4"><Badge className="bg-primary/10 text-primary">{u.role_name}</Badge></td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{branchNames(u.branch_ids)}</td>
                    <td className="py-3 px-4">{u.is_active === false ? <Badge className="bg-rose-100 text-rose-700">Nonaktif</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>}</td>
                    {canEdit && (
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setEditing(u)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil size={15} /></button>
                          <button onClick={() => resetPw(u)} className="w-8 h-8 rounded-lg hover:bg-amber-50 flex items-center justify-center text-amber-600" title="Reset password"><KeyRound size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && <UserModal user={editing} roles={roles} branches={branches} isOwner={isOwner} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

const EMAIL_DOMAIN = "aurora.id";
const slugEmail = (name) => name.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "").replace(/\.{2,}/g, ".");

function UserModal({ user, roles, branches, isOwner, onClose, onSaved }) {
  const isNew = !user.id;
  const [form, setForm] = useState({
    name: user.name || "", email: user.email || "", username: user.username || "", password: "",
    role_name: user.role_name || roles[0]?.name || "CASHIER", phone: user.phone || "",
    branch_ids: user.branch_ids || [], pin: "",
  });
  const [busy, setBusy] = useState(false);
  const [emailEdited, setEmailEdited] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id) => set("branch_ids", form.branch_ids.includes(id) ? form.branch_ids.filter((b) => b !== id) : [...form.branch_ids, id]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    if (isNew && (!form.email || form.password.length < 6)) return toast.error("Email & password (min 6) wajib");
    if (form.pin && !/^\d{4,8}$/.test(form.pin)) return toast.error("PIN harus 4–8 digit angka (atau kosongkan).");
    setBusy(true);
    try {
      const body = { name: form.name, role_name: form.role_name, phone: form.phone, branch_ids: form.branch_ids, username: form.username || null };
      if (form.pin) body.pin = form.pin;
      if (isNew) { body.email = form.email; body.password = form.password; await api.post("/auth/users", body); }
      else {
        if (form.password) body.password = form.password;
        if (isOwner && form.email && form.email.toLowerCase().trim() !== (user.email || "").toLowerCase().trim()) body.email = form.email.toLowerCase().trim();
        await api.patch(`/auth/users/${user.id}`, body);
      }
      toast.success(isNew ? "Karyawan dibuat" : "Karyawan diperbarui");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Karyawan Baru" : "Edit Karyawan"} maxWidth="max-w-md">
      <div className="space-y-3">
        <F label="Nama"><input value={form.name} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, name: v, ...(isNew && !emailEdited ? { email: v.trim() ? `${slugEmail(v)}@${EMAIL_DOMAIN}` : "" } : {}) })); }} className="uinp" data-testid="uf-name" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Email"><input value={form.email} onChange={(e) => { setEmailEdited(true); set("email", e.target.value); }} className="uinp" disabled={!isNew && !isOwner} placeholder="otomatis dari nama, bisa diubah" data-testid="uf-email" />{!isNew && isOwner && <span className="text-[11px] text-slate-400 mt-1 block">Owner dapat mengganti email login karyawan</span>}</F>
          <F label="Username (opsional)"><input value={form.username} onChange={(e) => set("username", e.target.value)} className="uinp" /></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label={isNew ? "Password" : "Password Baru (opsional)"}><input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="uinp" data-testid="uf-password" /></F>
          <F label="Peran"><select value={form.role_name} onChange={(e) => set("role_name", e.target.value)} className="uinp">{roles.map((r) => <option key={r.name} value={r.name}>{r.label || r.name}</option>)}</select></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Telepon"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="uinp" /></F>
          <F label="PIN (approval, opsional)"><input value={form.pin} onChange={(e) => set("pin", e.target.value)} className="uinp" placeholder="4-8 digit" /></F>
        </div>
        <div>
          <span className="text-sm font-medium text-slate-600 mb-1 block">Cabang</span>
          <div className="flex flex-wrap gap-2">
            {branches.map((b) => (
              <button key={b.id} onClick={() => toggleBranch(b.id)} className={`px-3 h-8 rounded-full text-xs font-medium ${form.branch_ids.includes(b.id) ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{b.name}</button>
            ))}
          </div>
        </div>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="uf-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
      <style>{`.uinp{width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #cbd5e1;border-radius:.75rem;font-size:.875rem;outline:none}.uinp:focus{border-color:hsl(var(--primary))}.uinp:disabled{background:#f1f5f9}`}</style>
    </Modal>
  );
}

const F = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>
);
